import React, { useEffect, useState } from 'react';
import { adminPurchaseRequestApi } from '../../api';
import { PurchaseRequest, PurchaseRequestStatus } from '../../types';
import { serviceFeeUsd, SERVICE_FEE_RATE, SERVICE_FEE_MIN_USD } from '../../config/pricing';

const STATUS_LABEL: Record<PurchaseRequestStatus, { text: string; color: string }> = {
  REQUESTED: { text: '신청됨', color: '#888' },
  QUOTED: { text: '견적됨', color: '#5c6bc0' },
  PAID: { text: '결제완료', color: '#2e7d32' },
  APPROVED: { text: '승인', color: '#4caf50' },
  REJECTED: { text: '거절', color: '#e53935' },
  CANCELLED: { text: '취소', color: '#bbb' },
  SHIPPED: { text: '발송됨', color: '#9c27b0' },
};

const AdminPurchaseRequests: React.FC = () => {
  const [list, setList] = useState<PurchaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [quoteId, setQuoteId] = useState<number | null>(null);
  const [itemPrice, setItemPrice] = useState('');
  const [serviceFee, setServiceFee] = useState('');
  const [shipping, setShipping] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [rejectId, setRejectId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [trackId, setTrackId] = useState<number | null>(null);
  const [trackNo, setTrackNo] = useState('');
  const [trackVideo, setTrackVideo] = useState('');
  const [extraId, setExtraId] = useState<number | null>(null);
  const [extraAmount, setExtraAmount] = useState('');
  const [extraReason, setExtraReason] = useState('');

  const total = (Number(itemPrice) || 0) + (Number(serviceFee) || 0) + (Number(shipping) || 0);

  const load = () => {
    setLoading(true);
    adminPurchaseRequestApi.getList()
      .then(r => setList(r.data.data.content))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openQuote = (r: PurchaseRequest) => {
    setQuoteId(r.id);
    // 상품가: 기존 견적 > 신청자 입력가(budget) 순. 수수료는 정책값(10%, 최소 $6) 자동 제안.
    const ip = r.itemPriceUsd != null ? Number(r.itemPriceUsd) : (r.budgetUsd != null ? Number(r.budgetUsd) : 0);
    setItemPrice(ip ? String(ip) : '');
    setServiceFee(r.serviceFeeUsd != null ? String(r.serviceFeeUsd) : (ip ? serviceFeeUsd(ip).toFixed(2) : ''));
    setShipping(r.shippingFeeUsd != null ? String(r.shippingFeeUsd) : '');
    setNote(r.adminNote ?? '');
  };

  // 상품가 입력 시 수수료(10%, 최소 $6) 자동 채움 (어드민이 덮어쓸 수 있음)
  const onItemPriceChange = (v: string) => {
    setItemPrice(v);
    const n = Number(v);
    if (n > 0) setServiceFee(serviceFeeUsd(n).toFixed(2));
  };

  const submitQuote = async () => {
    if (quoteId == null) return;
    if (!itemPrice || Number(itemPrice) < 0) { alert('상품 가격을 입력하세요.'); return; }
    setBusy(true);
    try {
      await adminPurchaseRequestApi.quote(quoteId, {
        itemPriceUsd: Number(itemPrice) || 0,
        serviceFeeUsd: Number(serviceFee) || 0,
        shippingFeeUsd: Number(shipping) || 0,
        adminNote: note.trim() || undefined,
      });
      setQuoteId(null);
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || '견적 저장 실패');
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (id: number, status: PurchaseRequestStatus) => {
    const labelMap: any = { APPROVED: '승인', REJECTED: '거절' };
    if (!window.confirm(`이 신청을 ${labelMap[status]}할까요?`)) return;
    try {
      await adminPurchaseRequestApi.updateStatus(id, status);
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || '상태 변경 실패');
    }
  };

  const submitReject = async () => {
    if (rejectId == null) return;
    if (!rejectReason.trim()) { alert('거절 사유를 입력하세요.'); return; }
    setBusy(true);
    try {
      await adminPurchaseRequestApi.updateStatus(rejectId, 'REJECTED', rejectReason.trim());
      setRejectId(null);
      setRejectReason('');
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || '거절 실패');
    } finally {
      setBusy(false);
    }
  };

  const openTrack = (r: PurchaseRequest) => {
    setTrackId(r.id);
    setTrackNo(r.trackingNumber ?? '');
    setTrackVideo(r.packingVideoUrl ?? '');
  };
  const submitTrack = async () => {
    if (trackId == null) return;
    if (!trackNo.trim()) { alert('운송장 번호를 입력하세요.'); return; }
    if (!trackVideo.trim()) { alert('포장 영상 링크를 첨부하세요.'); return; }
    setBusy(true);
    try {
      await adminPurchaseRequestApi.registerTracking(trackId, trackNo.trim(), trackVideo.trim());
      setTrackId(null);
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || '운송장 등록 실패');
    } finally { setBusy(false); }
  };

  const openExtra = (r: PurchaseRequest) => {
    setExtraId(r.id);
    setExtraAmount(r.extraChargeUsd != null && !r.extraChargePaid ? String(r.extraChargeUsd) : '');
    setExtraReason(r.extraChargePaid ? '' : (r.extraChargeReason ?? ''));
  };
  const submitExtra = async () => {
    if (extraId == null) return;
    if (!extraAmount || Number(extraAmount) <= 0) { alert('추가 청구 금액을 입력하세요.'); return; }
    setBusy(true);
    try {
      await adminPurchaseRequestApi.addExtraCharge(extraId, Number(extraAmount), extraReason.trim());
      setExtraId(null);
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || '추가 청구 실패');
    } finally { setBusy(false); }
  };

  return (
    <div style={s.page}>
      <h1 style={s.title}>대리구매 신청 관리</h1>
      {loading ? (
        <p style={s.muted}>불러오는 중...</p>
      ) : list.length === 0 ? (
        <p style={s.muted}>신청 내역이 없습니다.</p>
      ) : (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>#</th>
              <th style={s.th}>신청자</th>
              <th style={s.th}>상품</th>
              <th style={s.th}>옵션/수량</th>
              <th style={s.th}>예산</th>
              <th style={s.th}>견적</th>
              <th style={s.th}>상태</th>
              <th style={s.th}>처리</th>
            </tr>
          </thead>
          <tbody>
            {list.map(r => {
              const st = STATUS_LABEL[r.status];
              return (
                <tr key={r.id} style={s.tr}>
                  <td style={s.td}>{r.id}</td>
                  <td style={s.td}>{r.userEmail}</td>
                  <td style={s.td}>
                    <a href={r.productUrl} target="_blank" rel="noreferrer" style={s.link}>{r.productName}</a>
                    {r.memo && <div style={s.memo}>{r.memo}</div>}
                  </td>
                  <td style={s.td}>{r.optionText || '-'} / {r.quantity}</td>
                  <td style={s.td}>{r.budgetUsd != null ? `$${Number(r.budgetUsd).toFixed(2)}` : '-'}</td>
                  <td style={s.td}>{r.quotedPriceUsd != null ? `$${Number(r.quotedPriceUsd).toFixed(2)}` : '-'}</td>
                  <td style={s.td}><span style={{ ...s.badge, color: st.color, borderColor: st.color }}>{st.text}</span></td>
                  <td style={s.td}>
                    {(r.status === 'REQUESTED' || r.status === 'QUOTED') ? (
                      <div style={s.actions}>
                        <button style={s.quoteBtn} onClick={() => openQuote(r)}>견적</button>
                        <button style={s.approveBtn} onClick={() => changeStatus(r.id, 'APPROVED')}>승인</button>
                        <button style={s.rejectBtn} onClick={() => { setRejectId(r.id); setRejectReason(''); }}>거절</button>
                      </div>
                    ) : (r.status === 'PAID' || r.status === 'APPROVED' || r.status === 'SHIPPED') ? (
                      <div>
                        <div style={s.actions}>
                          <button style={s.quoteBtn} onClick={() => openTrack(r)}>{r.trackingNumber ? '운송장 수정' : '운송장 등록'}</button>
                          <button style={s.extraBtn} onClick={() => openExtra(r)}>추가 청구</button>
                        </div>
                        {r.trackingNumber && <div style={s.infoLine}>🚚 {r.trackingNumber}</div>}
                        {r.extraChargeUsd != null && Number(r.extraChargeUsd) > 0 && (
                          <div style={s.infoLine}>추가청구 ${Number(r.extraChargeUsd).toFixed(2)} · {r.extraChargePaid ? '결제완료' : '미결제'}</div>
                        )}
                      </div>
                    ) : <span style={s.muted}>-</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {quoteId != null && (
        <div style={s.backdrop}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>견적 입력</h3>
            <label style={s.label}>상품 가격 (USD)</label>
            <input style={s.input} type="number" min={0} step="0.01" value={itemPrice} onChange={e => onItemPriceChange(e.target.value)} />
            <label style={{ ...s.label, marginTop: 12 }}>수수료 (USD) — 기본 {SERVICE_FEE_RATE * 100}%, 최소 ${SERVICE_FEE_MIN_USD} 자동</label>
            <input style={s.input} type="number" min={0} step="0.01" value={serviceFee} onChange={e => setServiceFee(e.target.value)} />
            <label style={{ ...s.label, marginTop: 12 }}>배송비 (USD)</label>
            <input style={s.input} type="number" min={0} step="0.01" value={shipping} onChange={e => setShipping(e.target.value)} />
            <div style={s.totalBox}>합계(견적): <strong>${total.toFixed(2)}</strong></div>
            <label style={{ ...s.label, marginTop: 12 }}>메모 (선택)</label>
            <textarea style={{ ...s.input, height: 60, resize: 'none' }} value={note} onChange={e => setNote(e.target.value)} />
            <div style={s.modalActions}>
              <button style={s.cancelModalBtn} onClick={() => setQuoteId(null)} disabled={busy}>취소</button>
              <button style={s.saveBtn} onClick={submitQuote} disabled={busy}>{busy ? '...' : '견적 저장'}</button>
            </div>
          </div>
        </div>
      )}

      {trackId != null && (
        <div style={s.backdrop}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>운송장 등록</h3>
            <label style={s.label}>운송장 번호</label>
            <input style={s.input} value={trackNo} onChange={e => setTrackNo(e.target.value)} placeholder="EMS/등기 번호 (예: EE123456785KR)" />
            <label style={{ ...s.label, marginTop: 12 }}>포장 영상 URL (필수)</label>
            <input style={s.input} value={trackVideo} onChange={e => setTrackVideo(e.target.value)} placeholder="유튜브 등 영상 링크 (https://...)" />
            <div style={s.modalActions}>
              <button style={s.cancelModalBtn} onClick={() => setTrackId(null)} disabled={busy}>취소</button>
              <button style={s.saveBtn} onClick={submitTrack} disabled={busy}>{busy ? '...' : '저장'}</button>
            </div>
          </div>
        </div>
      )}

      {extraId != null && (
        <div style={s.backdrop}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>추가 청구</h3>
            <label style={s.label}>추가 청구 금액 (USD)</label>
            <input style={s.input} type="number" min={0} step="0.01" value={extraAmount} onChange={e => setExtraAmount(e.target.value)} />
            <label style={{ ...s.label, marginTop: 12 }}>사유 (선택)</label>
            <textarea style={{ ...s.input, height: 60, resize: 'none' }} value={extraReason} onChange={e => setExtraReason(e.target.value)} placeholder="예: 추가 배송비, 무게 초과 등 (고객에게 표시)" />
            <div style={s.modalActions}>
              <button style={s.cancelModalBtn} onClick={() => setExtraId(null)} disabled={busy}>취소</button>
              <button style={s.saveBtn} onClick={submitExtra} disabled={busy}>{busy ? '...' : '청구하기'}</button>
            </div>
          </div>
        </div>
      )}

      {rejectId != null && (
        <div style={s.backdrop}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>신청 거절</h3>
            <label style={s.label}>거절 사유 *</label>
            <textarea
              style={{ ...s.input, height: 90, resize: 'none' }}
              placeholder="거절 사유를 입력하세요 (신청자에게 표시됩니다)"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              autoFocus
            />
            <div style={s.modalActions}>
              <button style={s.cancelModalBtn} onClick={() => setRejectId(null)} disabled={busy}>취소</button>
              <button style={s.rejectConfirmBtn} onClick={submitReject} disabled={busy}>{busy ? '...' : '거절하기'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { padding: 8 },
  title: { fontSize: 22, fontWeight: 700, marginBottom: 20 },
  muted: { color: '#bbb', fontSize: 14 },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #f0f0f0', color: '#888', fontWeight: 600, whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f5f5f5' },
  td: { padding: '10px 12px', verticalAlign: 'top' },
  link: { color: '#5c6bc0', fontWeight: 600 },
  memo: { fontSize: 11, color: '#aaa', marginTop: 2 },
  badge: { fontSize: 11, fontWeight: 700, padding: '2px 8px', border: '1px solid', borderRadius: 10, whiteSpace: 'nowrap' },
  actions: { display: 'flex', gap: 6 },
  quoteBtn: { padding: '5px 10px', background: '#fff', color: '#5c6bc0', border: '1px solid #5c6bc0', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  approveBtn: { padding: '5px 10px', background: '#4caf50', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  rejectBtn: { padding: '5px 10px', background: '#fff', color: '#e53935', border: '1px solid #e53935', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  extraBtn: { padding: '5px 10px', background: '#fff', color: '#e65100', border: '1px solid #e65100', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  infoLine: { fontSize: 11, color: '#888', marginTop: 4 },
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { background: '#fff', borderRadius: 12, padding: 28, width: 420, maxWidth: '90%' },
  modalTitle: { fontSize: 17, fontWeight: 700, marginBottom: 16 },
  totalBox: { marginTop: 12, padding: '8px 12px', background: '#f5f9ff', border: '1px solid #d6e4f5', borderRadius: 8, fontSize: 14, color: '#1a5fa8' },
  label: { display: 'block', fontSize: 13, color: '#555', marginBottom: 6 },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #e8e8e8', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  modalActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 },
  cancelModalBtn: { padding: '10px 16px', background: '#f5f5f5', color: '#555', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  saveBtn: { padding: '10px 20px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  rejectConfirmBtn: { padding: '10px 20px', background: '#e53935', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};

export default AdminPurchaseRequests;
